import { getAuth } from "firebase/auth";
import { getExpressApiBaseUrl } from "./config";
import { isBase64Image, isImageUrl } from "./imageUploadAPI";
import { formatClumseAiBubbleText } from "../clumseChatWelcome";

/** Never send base64 blobs in chat history; URLs only (keeps payloads small and fast). */
function sanitizeImagesForApi(images) {
  if (!Array.isArray(images)) return [];
  return images.filter(
    (u) => typeof u === "string" && u.length > 0 && !isBase64Image(u) && isImageUrl(u)
  );
}

function toSendChatHistory(messages) {
  return messages.map((msg) => ({
    from: msg.role === "user" ? "user" : "assistant",
    text: msg.content,
    timestamp:
      typeof msg.timestamp === "string"
        ? msg.timestamp
        : new Date(msg.timestamp || Date.now()).toISOString(),
    images: sanitizeImagesForApi(msg.images),
  }));
}

/**
 * Send the current conversation to Clumse AI (same contract as AIAssistantWidget).
 * @param {Array<{ role: string, content: string, timestamp?: string | Date, images?: string[] }>} conversationMessages - Must end with the latest user message.
 * @param {{ chatId?: string, jobRequestId?: string, jobCompletion?: { percentage: number, isComplete: boolean, missing?: string[], completed?: string[], postVendorOutreach?: boolean } }} [options] - Optional client thread id, linked job id, and job completion state.
 * @returns {Promise<{ message: string, metadata?: object }>}
 */
export async function sendChatMessage(conversationMessages, options = {}) {
  const user = getAuth().currentUser;
  if (!user?.uid) {
    throw new Error("Please log in to continue.");
  }

  // Get Firebase auth token
  const token = await user.getIdToken();
  if (!token) {
    throw new Error("Authentication failed. Please try logging in again.");
  }

  const last = conversationMessages[conversationMessages.length - 1];
  if (!last || last.role !== "user") {
    throw new Error("Last message must be from the user.");
  }

  const messageText = String(last.content || "").trim();
  const images = Array.isArray(last.images)
    ? last.images.filter((u) => typeof u === "string" && u.length > 0)
    : [];
  if (!messageText && images.length === 0) {
    throw new Error("Add a message or at least one image.");
  }

  const chatHistory = toSendChatHistory(conversationMessages);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 90000);

  try {
    const response = await fetch(`${getExpressApiBaseUrl()}/sendChat`, {
      method: "POST",
      headers: { 
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      },
      body: JSON.stringify({
        clientId: user.uid,
        message: messageText,
        chatHistory,
        ...(options.chatId ? { chatId: options.chatId } : {}),
        ...(options.jobRequestId ? { jobRequestId: options.jobRequestId } : {}),
        ...(images.length ? { images } : {}),
        ...(options.jobCompletion ? { jobCompletion: options.jobCompletion } : {}),
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text().catch(() => "");
      // Parse server JSON for structured error details (reqId helps correlate backend logs)
      let serverDetail = {};
      try { serverDetail = JSON.parse(errorText); } catch { /* not JSON */ }
      const reqId = serverDetail._debug_reqId;
      console.debug(
        `[CLUMSE-API] sendChat HTTP ${response.status} error${reqId ? ` (reqId: ${reqId})` : ""}:`,
        serverDetail.error || errorText,
        "\nFull server response:", errorText
      );
      throw new Error(`API call failed: ${response.status} ${errorText}`);
    }

    const data = await response.json();

    if (data.error && data.status !== "consent_required") {
      throw new Error(data.error);
    }

    if (data.status === "consent_required" || data.consent_required) {
      return {
        message:
          "Please accept data processing in your account settings to continue using Clumse AI.",
      };
    }

    const text = formatClumseAiBubbleText(
      data.message?.text ||
        data.response ||
        (typeof data.message === "string" ? data.message : null) ||
        "Sorry, I could not generate a reply. Please try again."
    );

    return {
      message: text,
      metadata: {
        ...data,
        ...(data.conversationId ? { conversationId: data.conversationId } : {}),
      },
    };
  } catch (err) {
    clearTimeout(timeoutId);
    if (err.name === "AbortError") {
      throw new Error("Request timed out. Please try again.");
    }
    throw err;
  }
}
